import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { CssBaseline, ThemeProvider, useMediaQuery } from '@mui/material';
import { createAppTheme } from './theme';

type Mode = 'light' | 'dark' | 'system';

interface ColorModeContextValue {
  mode: Mode;
  resolved: 'light' | 'dark';
  setMode: (mode: Mode) => void;
}

const ColorModeContext = createContext<ColorModeContextValue | null>(null);
const STORAGE_KEY = 'hxiq.colorMode';

export function ColorModeProvider({ children }: { children: ReactNode }) {
  const prefersDark = useMediaQuery('(prefers-color-scheme: dark)');
  const [mode, setModeState] = useState<Mode>(() => {
    try {
      return (localStorage.getItem(STORAGE_KEY) as Mode | null) ?? 'system';
    } catch {
      // Private browsing and some embedded WebViews throw on access.
      return 'system';
    }
  });

  const resolved = mode === 'system' ? (prefersDark ? 'dark' : 'light') : mode;
  const theme = useMemo(() => createAppTheme(resolved), [resolved]);

  useEffect(() => {
    // Keeps the mobile browser chrome and the Capacitor status bar in sync.
    document
      .querySelector('meta[name="theme-color"]')
      ?.setAttribute('content', theme.palette.background.default);
  }, [theme]);

  const value = useMemo<ColorModeContextValue>(
    () => ({
      mode,
      resolved,
      setMode: (next) => {
        setModeState(next);
        try {
          localStorage.setItem(STORAGE_KEY, next);
        } catch {
          /* preference is not critical */
        }
      },
    }),
    [mode, resolved],
  );

  return (
    <ColorModeContext.Provider value={value}>
      <ThemeProvider theme={theme}>
        <CssBaseline enableColorScheme />
        {children}
      </ThemeProvider>
    </ColorModeContext.Provider>
  );
}

export function useColorMode(): ColorModeContextValue {
  const context = useContext(ColorModeContext);
  if (!context) throw new Error('useColorMode must be used within a ColorModeProvider');
  return context;
}
